import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import {
  extractErrorMessage,
  newAxiosClient,
  PageContainer,
  PageTopbar,
  StickyActionBar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type BreadcrumbItem,
} from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import ContactForm, { type IContactForm } from "./form";

interface ContactEditProps {
  currentUser: IUser;
}

// Seeded with blankContact (rather than null) so ContactForm can read
// formValues unconditionally — ContentContainer's `children` prop is
// constructed on every render regardless of `loading`, so a null initial
// value would throw before the fetch below ever resolves.
const blankContact: IContactForm = {
  firstName: "",
  lastName: "",
  email: "",
  status: "active",
};

const axios = newAxiosClient();

const ContactEdit = ({ currentUser }: ContactEditProps) => {
  const [formValues, setFormValues] = useState<IContactForm>(blankContact);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const params = useParams();
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Contacts", href: "/admin/contacts" },
    { label: "Edit" },
  ];

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await axios.get(`/api/contacts/${params.contactId}`, {
          headers: {
            AUTHORIZATION: `Bearer ${currentUser.authToken}`,
          },
        });
        const contact = res.data.contact;
        setFormValues({
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          status: contact.status,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, [params.contactId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.put(
        `/api/contacts/${params.contactId}`,
        { contact: formValues },
        {
          headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
          withCredentials: true,
        },
      );
      navigate("/admin/contacts");
    } catch (error) {
      setSubmitError(
        extractErrorMessage(error, "Failed to save the contact. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContactForm
          values={formValues}
          onChange={setFormValues}
          loading={loading}
        />

        <StickyActionBar error={submitError ?? undefined}>
          <astryx.Button
            label="Cancel"
            variant="ghost"
            onClick={() => navigate("/admin/contacts")}
          />
          <astryx.Button
            label="Save"
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit}
          />
        </StickyActionBar>
      </PageContainer>
    </astryx.Theme>
  );
};

export default ContactEdit;
